import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PDSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(sessionId ? "polling" : "idle");
  const [token, setToken] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const nav = useNavigate();

  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await api.get(`/pd/payment-status/${sessionId}`);
      if (data.payment_status === "paid" && data.access_token) {
        setStatus("paid");
        setToken(data.access_token);
        setTimeout(() => nav(`/pd/course?token=${data.access_token}`), 1500);
        return;
      }
      if (data.status === "expired") { setStatus("expired"); return; }
      setAttempts(a => a + 1);
    } catch (e) {
      setAttempts(a => a + 1);
    }
  }, [sessionId, nav]);

  useEffect(() => {
    if (!sessionId) return;
    if (status === "paid" || status === "expired") return;
    if (attempts >= 8) { setStatus("timeout"); return; }
    const t = setTimeout(poll, attempts === 0 ? 800 : 2000);
    return () => clearTimeout(t);
  }, [attempts, sessionId, status, poll]);

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16">
      <div className="bg-white rounded-3xl p-10 border border-slate-100 shadow-[0_20px_60px_rgba(124,58,237,0.12)] max-w-md w-full text-center">
        {(status === "polling" || status === "idle") && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-violet-500 animate-spin mb-4" strokeWidth={2.5}/>
            <h1 className="text-3xl font-black text-slate-900">Confirming payment…</h1>
            <p className="text-slate-500 mt-2">Hang tight, we're checking with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="text-emerald-600" strokeWidth={2.5}/>
            </div>
            <h1 className="text-3xl font-black text-slate-900">You're in!</h1>
            <p className="text-slate-600 mt-2">Welcome to AI Foundation for Teachers. Sending you to the course…</p>
          </>
        )}
        {status === "timeout" && (
          <>
            <Sparkles className="w-12 h-12 mx-auto text-amber-500 mb-4"/>
            <h1 className="text-3xl font-black text-slate-900">Still processing</h1>
            <p className="text-slate-600 mt-2">Stripe is taking a bit longer. Check your email — we'll send the course link as soon as payment confirms.</p>
            <Link to="/professional-development"><Button className="mt-6 rounded-full">Back to course page</Button></Link>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-rose-500 mb-4"/>
            <h1 className="text-3xl font-black text-slate-900">Session expired</h1>
            <Link to="/professional-development"><Button className="mt-6 rounded-full">Try again</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
